import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input } from '@components/common/UIComponents';
import { getUserSettings, updateUserSettings } from '@services/firestoreService';
import { Settings as SettingsIcon, Target, Droplets, Activity, Heart, User, Save } from 'lucide-react';

const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const [formData, setFormData] = useState({
    // Profile
    age: 39,
    gender: 'M',
    heightFeet: 5,
    heightInches: 4,
    activityLevel: 'Moderate',
    weightGoal: 'Lose',
    // Goals
    stepsGoal: 8000,
    heartPointsGoal: 20,
    waterGoal: 3000,
    // Legacy
    height: 170,
    weightUnit: 'kg'
  });

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const settings = await getUserSettings(user.uid);
      if (settings) {
        setFormData({
          // Profile
          age: settings.age || 39,
          gender: settings.gender || 'M',
          heightFeet: settings.heightFeet || 5,
          heightInches: settings.heightInches || 4,
          activityLevel: settings.activityLevel || 'Moderate',
          weightGoal: settings.weightGoal || 'Lose',
          // Goals
          stepsGoal: settings.stepsGoal || 8000,
          heartPointsGoal: settings.heartPointsGoal || 20,
          waterGoal: settings.waterGoal || 3000,
          // Legacy
          height: settings.height || 170,
          weightUnit: settings.weightUnit || 'kg'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await updateUserSettings(user.uid, formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Customize your fitness goals and preferences
        </p>
      </div>

      {/* Profile Information */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <User className="text-primary-600 dark:text-primary-400" size={24} />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Profile Information
          </h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
              Display Name
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium">
              {user?.displayName || 'User'}
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
              Email
            </label>
            <p className="text-gray-900 dark:text-gray-100">
              {user?.email}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              name="age"
              label="Age (years)"
              value={formData.age}
              onChange={handleInputChange}
              fullWidth
            />

            <div>
              <label className="block text-sm font-medium mb-2">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Height</label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                name="heightFeet"
                label="Feet"
                value={formData.heightFeet}
                onChange={handleInputChange}
                placeholder="5"
                fullWidth
              />
              <Input
                type="number"
                name="heightInches"
                label="Inches"
                value={formData.heightInches}
                onChange={handleInputChange}
                placeholder="4"
                fullWidth
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Activity Level</label>
            <select
              name="activityLevel"
              value={formData.activityLevel}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="Sedentary">Sedentary (Little or no exercise)</option>
              <option value="Light">Light (Exercise 1-3 days/week)</option>
              <option value="Moderate">Moderate (Exercise 3-5 days/week)</option>
              <option value="Active">Active (Exercise 6-7 days/week)</option>
              <option value="VeryActive">Very Active (Hard exercise daily + physical job)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Weight Goal</label>
            <select
              name="weightGoal"
              value={formData.weightGoal}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="Lose">Lose Weight (500 cal deficit)</option>
              <option value="Maintain">Maintain Weight</option>
              <option value="Gain">Gain Weight (400 cal surplus)</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Daily Goals */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <Target className="text-green-600 dark:text-green-400" size={24} />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Daily Goals
          </h2>
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Activity className="text-primary-600 dark:text-primary-400" size={24} />
            </div>
            <Input
              type="number"
              name="stepsGoal"
              label="Steps Goal"
              value={formData.stepsGoal}
              onChange={handleInputChange}
              fullWidth
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <Heart className="text-red-600 dark:text-red-400" size={24} />
            </div>
            <Input
              type="number"
              name="heartPointsGoal"
              label="Heart Points Goal"
              value={formData.heartPointsGoal}
              onChange={handleInputChange}
              fullWidth
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Droplets className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <Input
              type="number"
              name="waterGoal"
              label="Water Goal (ml)"
              value={formData.waterGoal}
              onChange={handleInputChange}
              fullWidth
            />
          </div>
        </div>
      </Card>

      {/* Recommendations */}
      <Card className="bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 border-primary-200 dark:border-primary-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          💡 Recommended Daily Goals
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li>• <strong>Steps:</strong> 8,000-10,000 steps for general health</li>
          <li>• <strong>Heart Points:</strong> 20+ points (1 point per minute of moderate activity)</li>
          <li>• <strong>Water:</strong> 2,500-3,500ml (adjust based on activity level)</li>
        </ul>
      </Card>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <Button
          onClick={handleSave}
          loading={loading}
          disabled={loading}
          size="lg"
        >
          <Save size={20} className="mr-2" />
          Save Settings
        </Button>
        
        {saved && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Settings saved!</span>
          </div>
        )}
      </div>

      {/* App Info */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          About FitNet
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Version 1.0.0 • Privacy-first fitness tracking
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Your data is stored securely and is only accessible to you.
        </p>
      </Card>
    </div>
  );
};

export default SettingsPage;
